/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { ActionButton, BrandColors, Modal, Text, Dropdown } from '@blend/enterprise-components';
import { setRoomError } from '../../../../actions/roomActions';
import { NotaryAPI } from '../../../../api/notary/types';
import { notaryAPI } from '../../../../api/notary';
import { useNotaryTasks } from '../../../../hooks/useNotaryTasks';
import { NotaryTasks } from './NotaryTasksCard';
import { ModalContent } from '@components/common/enterprise/ModalContent/ModalContent';
import { cloneDeep } from '@utils/lodash';

const styles = StyleSheet.create({
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 14,
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
  },
  submitWrapper: {
    marginLeft: 8,
  },
  removeButton: {
    cursor: 'pointer',
    color: BrandColors.NEUTRAL_500,
    fontSize: 14,
    ':focus-visible': {
      outline: 'none',
      backgroundColor: BrandColors.NEUTRAL_100,
    },
  },
  editSection: {
    width: '50%',
    marginTop: 25,
    marginBottom: 40,
  },
  dropdownWrapper: {
    marginTop: 20,
    marginBottom: 20,
  },
});

export const taskToStatusListMapping: Record<keyof NotaryTasks, string[]> = {
  ['Lender Documents']: ['Pending Tagging', 'Incomplete/Missing', 'Tagged'],
  ['Title Documents']: ['Pending Tagging', 'Pending Docs', 'Received and Tagged', 'N/A'],
};

export namespace NotaryTasksModal {
  export interface Props {
    headerText: string;
    subHeaderText?: string;
    tasks: NotaryAPI.NotaryTasksResponse[];
    roomId: string;
    handleClose: () => void;
  }
}

export const NotaryTasksModal: React.FC<NotaryTasksModal.Props> = ({
  headerText,
  subHeaderText,
  tasks,
  roomId,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { dispatchNotaryTasks } = useNotaryTasks(roomId);
  const updatedTasks: NotaryAPI.NotaryTasksResponse[] = cloneDeep(tasks);
  const onSubmit = async () => {
    try {
      const tasksToUpdate = updatedTasks.filter(updatedTask => {
        return tasks.find(originalTask => originalTask.name === updatedTask.name)?.status !== updatedTask.status;
      });
      await Promise.all(
        tasksToUpdate.map(async task => {
          await notaryAPI.rooms.updateRoomNotaryTask(task.id, { status: task.status });
        }),
      );
    } catch (error) {
      setRoomError({ hasError: true })(dispatch);
    } finally {
      await dispatchNotaryTasks();
      handleClose();
    }
  };
  const statusListToDropdownConfig = (statuses: string[]) => {
    const configs = statuses.map(status => {
      const config: Dropdown.Option = {
        text: status,
        value: status,
      };
      return config;
    });
    return configs;
  };
  const handleChange = (taskName: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value;
    updatedTasks.forEach((task, idx) => {
      if (task.name === taskName) {
        updatedTasks[idx] = {
          ...task,
          status,
        };
      }
    });
  };
  return (
    <Modal data-it="notaryTasksModal" contentLabel={headerText} handleClose={handleClose} isOpen={true}>
      <ModalContent handleClose={handleClose} headerText={headerText} subHeaderText={subHeaderText}>
        <Form onSubmit={onSubmit}>
          {({ handleSubmit, submitting }) => {
            return (
              <div>
                <div className={css(styles.editSection)}>
                  {tasks.map(task => {
                    return (
                      <div key={task.id} data-it={`notaryTaskEdit-${task.id}`} className={css(styles.dropdownWrapper)}>
                        <Text>{task.name} status</Text>
                        <Dropdown
                          data-it={`notaryTaskDropdown-${task.id}`}
                          options={statusListToDropdownConfig(taskToStatusListMapping[task.name as keyof NotaryTasks])}
                          onChange={handleChange(task.name)}
                          valueRequired={true}
                          defaultValue={task.status}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className={css(styles.footerBar)}>
                  <ActionButton
                    data-dd-privacy="allow"
                    data-it="notaryTasksModal-cancel"
                    label="Cancel"
                    onClick={handleClose}
                    theme="secondary"
                  />
                  <div className={css(styles.submitWrapper)}>
                    <ActionButton
                      data-dd-privacy="allow"
                      disabled={submitting}
                      data-it="notaryTasksModal-submit"
                      label="Save"
                      onClick={handleSubmit}
                    />
                  </div>
                </div>
              </div>
            );
          }}
        </Form>
      </ModalContent>
    </Modal>
  );
};
